import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import {
  CpuChipIcon,
  ClockIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const PerformanceContainer = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const PerformanceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const PerformanceTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.border.secondary};
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const MetricCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MetricValue = styled.div`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`;

const MetricUnit = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.normal};
  color: ${theme.colors.text.secondary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 0.5rem;
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.primary.main};
  width: ${props => Math.min(props.value, 100)}%;
  transition: width 0.3s ease;
`;

const AlertsSection = styled.div`
  border-top: 1px solid ${theme.colors.border.secondary};
  padding-top: 1rem;
`;

const AlertsTitle = styled.h4`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  margin: 0 0 1rem 0;
`;

const AlertItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  border: 1px solid ${theme.colors.border.secondary};
`;

const DashboardPerformance = ({ dashboardId }) => {
  const [performance, setPerformance] = useState({
    overallStatus: 'good',
    loadTime: 0,
    dataLatency: 0,
    updateSuccess: 0,
    memoryUsage: 0,
    cpuUsage: 0,
    networkLatency: 0,
    errorRate: 0
  });

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (dashboardId) {
      fetchPerformanceData();
      const interval = setInterval(fetchPerformanceData, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    }
  }, [dashboardId]);

  const fetchPerformanceData = async () => {
    try {
      // Simulate performance data
      const mockPerformance = {
        overallStatus: getRandomStatus(),
        loadTime: Math.random() * 2000 + 500, // 500-2500ms
        dataLatency: Math.random() * 1000 + 100, // 100-1100ms
        updateSuccess: Math.random() * 20 + 80, // 80-100%
        memoryUsage: Math.random() * 40 + 30, // 30-70%
        cpuUsage: Math.random() * 30 + 10, // 10-40%
        networkLatency: Math.random() * 200 + 50, // 50-250ms
        errorRate: Math.random() * 5 // 0-5%
      };

      setPerformance(mockPerformance);
      generateAlerts(mockPerformance);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      setLoading(false);
    }
  };

  const getRandomStatus = () => {
    const statuses = ['excellent', 'good', 'warning', 'poor'];
    const weights = [0.4, 0.4, 0.15, 0.05]; // Bias towards good performance
    const random = Math.random();
    let sum = 0;
    
    for (let i = 0; i < statuses.length; i++) {
      sum += weights[i];
      if (random <= sum) return statuses[i];
    }
    return 'good';
  };

  const generateAlerts = (perf) => {
    const newAlerts = [];
    
    if (perf.loadTime > 2000) {
      newAlerts.push({
        type: 'warning',
        message: 'Dashboard load time is slower than expected'
      });
    }
    
    if (perf.dataLatency > 800) {
      newAlerts.push({
        type: 'warning',
        message: 'Data latency is higher than normal'
      });
    }
    
    if (perf.updateSuccess < 90) {
      newAlerts.push({
        type: 'error',
        message: 'Data update success rate is below threshold'
      });
    }
    
    if (perf.memoryUsage > 60) {
      newAlerts.push({
        type: 'warning',
        message: 'Memory usage is elevated'
      });
    }
    
    if (perf.errorRate > 3) {
      newAlerts.push({
        type: 'error',
        message: 'Error rate is above acceptable levels'
      });
    }
    
    if (newAlerts.length === 0) {
      newAlerts.push({
        type: 'success',
        message: 'All systems operating normally'
      });
    }
    
    setAlerts(newAlerts);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent':
      case 'good':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'warning':
      case 'poor':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <SignalIcon className="w-4 h-4" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'warning': return 'Warning';
      case 'poor': return 'Poor';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <PerformanceContainer>
        <PerformanceHeader>
          <PerformanceTitle>
            <CpuChipIcon className="w-5 h-5" />
            Performance Monitor
          </PerformanceTitle>
        </PerformanceHeader>
        <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
          Loading performance data...
        </div>
      </PerformanceContainer>
    );
  }

  return (
    <PerformanceContainer>
      <PerformanceHeader>
        <PerformanceTitle>
          <CpuChipIcon className="w-5 h-5" />
          Performance Monitor
        </PerformanceTitle>
        <StatusIndicator status={performance.overallStatus}>
          {getStatusIcon(performance.overallStatus)}
          {getStatusText(performance.overallStatus)}
        </StatusIndicator>
      </PerformanceHeader>

      <MetricsGrid>
        <MetricCard>
          <MetricHeader>
            <MetricLabel>
              <ClockIcon className="w-4 h-4" />
              Load Time
            </MetricLabel>
          </MetricHeader>
          <MetricValue>
            {performance.loadTime.toFixed(0)}
            <MetricUnit>ms</MetricUnit>
          </MetricValue>
          <ProgressBar>
            <ProgressFill value={Math.max(0, 100 - (performance.loadTime / 30))} />
          </ProgressBar>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricLabel>
              <SignalIcon className="w-4 h-4" />
              Data Latency
            </MetricLabel>
          </MetricHeader>
          <MetricValue>
            {performance.dataLatency.toFixed(0)}
            <MetricUnit>ms</MetricUnit>
          </MetricValue>
          <ProgressBar>
            <ProgressFill value={Math.max(0, 100 - (performance.dataLatency / 15))} />
          </ProgressBar>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricLabel>
              <CheckCircleIcon className="w-4 h-4" />
              Update Success
            </MetricLabel>
          </MetricHeader>
          <MetricValue>
            {performance.updateSuccess.toFixed(1)}
            <MetricUnit>%</MetricUnit>
          </MetricValue>
          <ProgressBar>
            <ProgressFill value={performance.updateSuccess} />
          </ProgressBar>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricLabel>
              <CpuChipIcon className="w-4 h-4" />
              CPU Usage
            </MetricLabel>
          </MetricHeader>
          <MetricValue>
            {performance.cpuUsage.toFixed(1)}
            <MetricUnit>%</MetricUnit>
          </MetricValue>
          <ProgressBar>
            <ProgressFill value={performance.cpuUsage} />
          </ProgressBar>
        </MetricCard>
      </MetricsGrid>

      <AlertsSection>
        <AlertsTitle>System Alerts</AlertsTitle>
        {alerts.map((alert, index) => (
          <AlertItem key={index} type={alert.type}>
            {alert.type === 'success' && <CheckCircleIcon className="w-4 h-4" />}
            {alert.type === 'warning' && <ExclamationTriangleIcon className="w-4 h-4" />}
            {alert.type === 'error' && <ExclamationTriangleIcon className="w-4 h-4" />}
            {alert.message}
          </AlertItem>
        ))}
      </AlertsSection>
    </PerformanceContainer>
  );
};

export default DashboardPerformance;